import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';

/**
 * Calculation Audit grid (Layer 3) — wide table of every input column for
 * every reservation, suitable for CPA / outside counsel review.
 *
 * Backed by GET /api/2.6/escrow/report (same endpoint as the main page; the
 * row payload now includes all calculation transparency fields).
 */
@Component({
	selector: 'app-escrow-report-audit',
	templateUrl: './escrow-report-audit.component.html',
	styleUrls: ['./escrow-report-audit.component.css'],
	standalone: false,
	providers: [MessageService]
})
export class EscrowReportAuditComponent implements OnInit {

	@ViewChild('auditTable') auditTable: Table;
	@ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;

	formLoading = false;

	asOfDate: Date;
	recentDays = 30;

	rows: any[] = [];
	filteredRows: any[] = [];
	marketFilter: string = 'ALL';

	marketOptions = [
		{ label: 'All markets', value: 'ALL' },
		{ label: 'Hawaii', value: 'HAWAII' },
		{ label: 'Utah', value: 'UTAH' }
	];

	constructor(
		private http: HttpClient,
		private router: Router,
		private messageService: MessageService) {
	}

	ngOnInit(): void {
		const now = new Date();
		this.asOfDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		this.loadReport();
	}

	loadReport(): void {
		this.formLoading = true;
		const asOfStr = moment(this.asOfDate).format('YYYY-MM-DD');
		const url = `${environment.apiUrl}/escrow/report?asOfDate=${asOfStr}&recentDays=${this.recentDays}`;
		this.http.get<ApiResponse>(url).subscribe({
			next: (res) => {
				try {
					const data = (res && res.data && (res.data as any).escrowReport) ? (res.data as any).escrowReport : null;
					if (data) {
						this.rows = data.rows || [];
						this.applyFilters();
						this.messageService.add({ severity: 'success', summary: 'Loaded', detail: `${this.rows.length} reservations`, life: 2500 });
					} else {
						this.rows = [];
						this.filteredRows = [];
					}
				} catch (e) {
					console.error('escrow-report-audit parse error', e);
				}
				this.formLoading = false;
			},
			error: (err) => {
				console.error('escrow-report-audit load error', err);
				this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message || 'Could not load audit data' });
				this.formLoading = false;
			}
		});
	}

	applyFilters(): void {
		this.filteredRows = (this.rows || []).filter(r => {
			if (this.marketFilter !== 'ALL' && r.market !== this.marketFilter) {
				return false;
			}
			return true;
		});
	}

	onAsOfChange(): void {
		if (this.asOfDate) {
			const d = this.asOfDate;
			this.asOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
		}
		this.loadReport();
	}

	gotoMain(): void {
		this.router.navigate(['/escrow-report']);
	}

	formatCurrency(v: any): string {
		if (v == null) return '';
		const n = typeof v === 'number' ? v : Number(v);
		if (isNaN(n)) return '';
		return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	formatNumber(v: any): string {
		if (v == null) return '';
		const n = typeof v === 'number' ? v : Number(v);
		if (isNaN(n)) return '';
		return n.toLocaleString('en-US');
	}

	formatDate(d: any): string {
		if (!d) return '';
		return moment(d).format('MM/DD/YYYY');
	}

	formatRatio(v: any): string {
		if (v == null) return '';
		const n = typeof v === 'number' ? v : Number(v);
		if (isNaN(n)) return '';
		// Display escrow ratio as percent with 2 decimals (e.g., 0.96 → 96.00%)
		return (n * 100).toFixed(2) + '%';
	}

	bankTrailLabel(s: string): string {
		switch (s) {
			case 'IN_ESCROW': return 'In escrow';
			case 'NO_BANK_TRAIL': return 'No bank trail';
			case 'MIXED': return 'Mixed';
			case 'NO_PAYMENTS': return 'No payments';
			default: return s || 'Unknown';
		}
	}

	downloadExcel(): void {
		if (!this.filteredRows || this.filteredRows.length === 0) {
			this.messageService.add({ severity: 'warn', summary: 'No data', detail: 'Nothing to export' });
			return;
		}
		const flat = this.filteredRows.map(r => ({
			confirmationNumber: r.confirmationNumber,
			guestName: r.guestName,
			propertyCode: r.propertyCode,
			market: r.market,
			reservationStatus: r.reservationStatus,
			checkInDate: r.checkInDate ? moment(r.checkInDate).format('YYYY-MM-DD') : '',
			checkOutDate: r.checkOutDate ? moment(r.checkOutDate).format('YYYY-MM-DD') : '',
			totalNights: r.totalNights,
			nightsConsumed: r.nightsConsumed,
			nightsRemaining: r.nightsRemaining,
			totalBilled: r.totalBilled,
			escrowEligibleBilled: r.escrowEligibleBilled,
			nonEscrowBilled: r.nonEscrowBilled,
			'Payment Received': r.netCashHeld, // Change 2: renamed from netCashHeld
			allocatedNetCash: r.allocatedNetCash,
			escrowRatio: r.escrowRatio,
			escrowEligibleCash: r.escrowEligibleCash,
			perNightEscrow: r.perNightEscrow,
			releasedToDate: r.releasedToDate,
			requiredInEscrow: r.requiredInEscrow,
			exception: r.exception ? 'Y' : 'N',
			bankTrailStatus: this.bankTrailLabel(r.bankTrailStatus),
			qboPostedAmount: r.qboPostedAmount,
			discrepancy: r.discrepancy
		}));
		this.importExportComponent.exporData('excel', flat, 'escrow-audit-' + moment(this.asOfDate).format('YYYY-MM-DD'));
	}

	downloadPdf(): void {
		this.formLoading = true;
		const asOfStr = moment(this.asOfDate).format('YYYY-MM-DD');
		// Reuse main PDF endpoint; future enhancement may add ?detail=full
		const url = `${environment.apiUrl}/escrow/report/pdf?asOfDate=${asOfStr}&recentDays=${this.recentDays}`;
		this.http.get(url, { responseType: 'blob' }).subscribe({
			next: (blob: Blob) => {
				FileSaver.saveAs(blob, 'escrow-audit-' + asOfStr + '.pdf');
				this.formLoading = false;
				this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'PDF downloaded', life: 2500 });
			},
			error: (err) => {
				console.error('escrow-report-audit pdf error', err);
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not download PDF' });
				this.formLoading = false;
			}
		});
	}
}
